import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import SectionHeader from '@/components/base/SectionHeader';
import { EmptyState, LoadingState } from '@/components/base/StateBlocks';
import VideoCard from '@/components/feature/VideoCard';
import DemoPlayer from '@/pages/detail/components/DemoPlayer';
import EpisodeList from '@/pages/detail/components/EpisodeList';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useFavorites } from '@/hooks/useFavorites';
import { useSimulatedLoad } from '@/hooks/useSimulatedLoad';
import type { Episode } from '@/types/video';
import { formatPlayCount, getRelatedVideos, getVideoById, typeNameMap } from '@/utils/videos';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const video = id ? getVideoById(id) : undefined;

  const [currentEpisode, setCurrentEpisode] = useState<Episode | undefined>(video?.episodes[0]);
  const [toast, setToast] = useState('');
  const playerRef = useRef<HTMLDivElement>(null);
  const { isFavorite, toggleFavorite } = useFavorites();
  const { loading } = useSimulatedLoad(400);

  useEffect(() => {
    setCurrentEpisode(video?.episodes[0]);
  }, [id, video]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(''), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useDocumentMeta({
    title: video ? `${video.title} - 在线观看 - 红桃视频` : '视频不存在 - 红桃视频',
    description: video
      ? `${video.title}（${video.year}·${video.region}·${typeNameMap[video.type]}）在线观看：${video.synopsis.slice(0, 60)}…`
      : '该视频不存在或已下架',
    keywords: video ? `${video.title},在线观看,红桃视频,${video.tags.join(',')}` : '红桃视频',
  });

  if (!video) {
    return (
      <div className="w-full px-4 md:px-8 py-16">
        <EmptyState
          title="该视频不存在或已下架"
          description="换个关键词搜索，或返回首页看看其他内容"
          icon="ri-file-warning-line"
        />
        <div className="flex justify-center gap-3">
          <Link
            to="/"
            className="px-5 py-2.5 rounded-md bg-primary-500 text-background-950 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
          >
            返回首页
          </Link>
          <Link
            to="/search"
            className="px-5 py-2.5 rounded-md bg-background-200/70 text-foreground-900 text-sm font-semibold whitespace-nowrap hover:bg-background-300/70 transition-colors"
          >
            去搜索
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelatedVideos(video, 6);
  const favorite = isFavorite(video.id);
  const metaItems = [
    { label: '类型', value: typeNameMap[video.type] },
    { label: '地区', value: video.region },
    { label: '年份', value: String(video.year) },
    { label: '时长', value: video.duration },
    { label: '状态', value: video.status },
    { label: '清晰度', value: video.quality },
  ];

  const handleFavorite = () => {
    const next = toggleFavorite(video.id);
    setToast(next ? '已加入我的收藏（已保存到本地）' : '已取消收藏');
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast('链接已复制，快去分享吧');
    } catch {
      setToast('复制失败，请手动复制浏览器地址');
    }
  };

  const scrollToPlayer = () => {
    playerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: typeNameMap[video.type], to: `/category/${video.type}` },
          { label: video.title },
        ]}
      />

      <section className="relative mt-4 rounded-xl overflow-hidden border border-background-200/70">
        <img
          src={video.backdrop}
          alt={`${video.title} 剧照`}
          title={`${video.title} 在线观看`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/90 to-background-50/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent" />

        <div className="relative p-5 md:p-8 flex flex-col md:flex-row gap-6 md:gap-8">
          <div className="w-28 md:w-44 shrink-0 aspect-[2/3] rounded-lg overflow-hidden border border-background-200/60 shadow-none">
            <img
              src={video.cover}
              alt={`${video.title} 封面`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="min-w-0 flex-1">
            <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950">
              {video.title}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              {metaItems.map((item) => (
                <span
                  key={item.label}
                  className="px-2.5 py-1 rounded-md bg-background-200/60 text-xs text-foreground-700"
                >
                  {item.value}
                </span>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
              <span className="flex items-center gap-1 text-accent-400 font-semibold">
                <i className="ri-star-fill" />
                {video.rating.toFixed(1)}
                <span className="text-foreground-500 font-normal">分</span>
              </span>
              <span className="text-foreground-500">{formatPlayCount(video.playCount)}</span>
              <span className="flex items-center gap-1 text-foreground-500">
                <i className="ri-user-star-line" />
                导演：{video.director}
              </span>
            </div>

            <p className="mt-3 text-sm text-foreground-600">
              <span className="text-foreground-500">主演：</span>
              {video.cast.join(' / ')}
            </p>

            <div className="mt-3 flex flex-wrap gap-2">
              {video.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="px-2.5 py-0.5 rounded-full bg-secondary-500/15 text-secondary-300 text-xs hover:bg-secondary-500/25 transition-colors"
                >
                  {tag}
                </Link>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={scrollToPlayer}
                className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-950 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-play-fill text-lg" />
                立即播放
              </button>
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  favorite
                    ? 'bg-accent-500 text-background-950'
                    : 'bg-background-200/70 text-foreground-900 hover:bg-background-300/70'
                }`}
              >
                <i className={favorite ? 'ri-heart-3-fill text-lg' : 'ri-heart-3-line text-lg'} />
                {favorite ? '已收藏' : '收藏'}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-200/70 text-foreground-900 text-sm font-semibold whitespace-nowrap hover:bg-background-300/70 transition-colors cursor-pointer"
              >
                <i className="ri-share-forward-line text-lg" />
                分享
              </button>
            </div>
          </div>
        </div>
      </section>

      {loading ? (
        <LoadingState text="正在加载视频详情…" />
      ) : (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-8">
          <div className="min-w-0 flex flex-col gap-6">
            <div ref={playerRef}>
              <DemoPlayer
                poster={video.backdrop}
                title={video.title}
                episodeTitle={currentEpisode ? currentEpisode.title : '正片'}
              />
            </div>

            <section className="rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">剧情简介</h2>
              <p className="mt-3 text-sm md:text-base text-foreground-700 leading-relaxed">
                {video.synopsis}
              </p>
            </section>

            <EpisodeList
              episodes={video.episodes}
              currentId={currentEpisode?.id ?? ''}
              onChange={setCurrentEpisode}
            />

            <section>
              <SectionHeader
                title="相关推荐"
                subtitle={`与《${video.title}》同类型或同主创的作品`}
                icon="ri-movie-2-line"
                moreTo={`/category/${video.type}`}
              />
              {related.length === 0 ? (
                <EmptyState title="暂无相关推荐" icon="ri-movie-line" />
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {related.map((item) => (
                    <VideoCard key={item.id} video={item} />
                  ))}
                </div>
              )}
            </section>
          </div>

          <aside className="flex flex-col gap-5">
            <div className="rounded-lg bg-background-100 border border-background-200/70 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-4">视频信息</h2>
              <dl className="flex flex-col gap-3 text-sm">
                {metaItems.map((item) => (
                  <div key={item.label} className="flex items-start justify-between gap-4">
                    <dt className="text-foreground-500 shrink-0">{item.label}</dt>
                    <dd className="text-foreground-800 text-right">{item.value}</dd>
                  </div>
                ))}
                <div className="flex items-start justify-between gap-4">
                  <dt className="text-foreground-500 shrink-0">导演</dt>
                  <dd className="text-foreground-800 text-right">{video.director}</dd>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <dt className="text-foreground-500 shrink-0">集数</dt>
                  <dd className="text-foreground-800 text-right">{video.episodes.length} 集</dd>
                </div>
              </dl>
            </div>

            <div className="rounded-lg bg-background-100 border border-background-200/70 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">观看提示</h2>
              <ul className="mt-3 flex flex-col gap-3 text-sm text-foreground-700">
                <li className="flex items-start gap-2">
                  <i className="ri-information-line text-primary-400 mt-0.5" />
                  <span>当前播放区为前端演示，暂未接入真实视频源</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-heart-3-line text-primary-400 mt-0.5" />
                  <span>收藏记录保存在浏览器本地，刷新后依然保留</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-share-forward-line text-primary-400 mt-0.5" />
                  <span>点击分享可复制本页链接，与朋友一起追剧</span>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[80] px-5 py-3 rounded-md bg-background-200 text-foreground-950 text-sm border border-background-300/60">
          {toast}
        </div>
      )}
    </div>
  );
}